import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Add,
  Print,
  Edit,
  ExpandMore,
  Science,
  Verified,
  Cancel,
  TrendingUp,
  TrendingDown,
  Analytics,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchQualityChecks,
  fetchDefectRecords,
  fetchDefectTypes,
  fetchQualityMetrics,
  createInspection,
  createDefect,
  clearError,
  setSelectedCheck,
  fetchControlPlans,
  createControlPlan,
  updateControlPlan,
  updateDefectStatus,
} from '../../store/slices/qualitySlice';
import SPCCharts from '../SPC/SPCCharts';
import PageHeader from '../../components/PageHeader/PageHeader';

const REPORT_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

// Types — kept identical to the slice interfaces so they are interchangeable
interface QualityCheck {
  id: string;
  checkNumber: string;
  workOrderId: string;
  productCode: string;
  productName: string;
  inspector: string;
  checkDate: string;
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'on_hold';
  checkType: 'incoming' | 'in_process' | 'final' | 'audit';
  parameters: QualityParameter[];
  overallResult: 'pass' | 'fail' | 'pending';
  notes?: string;
}

interface QualityParameter {
  id: string;
  name: string;
  specification: string;
  measuredValue: string;
  tolerance: string;
  status: 'pass' | 'fail' | 'warning';
  unit: string;
}

interface DefectRecord {
  id: string;
  workOrderId: string;
  productCode: string;
  defectType: string;
  defectDescription: string;
  severity: 'minor' | 'major' | 'critical';
  quantity: number;
  reportedBy: string;
  reportedDate: string;
  status: 'open' | 'investigating' | 'resolved' | 'closed';
  rootCause?: string;
  correctiveAction?: string;
}

interface ControlPlan {
  id: string;
  planName: string;
  itemId: string;
  version: string;
  status: 'active' | 'inactive';
  description?: string;
  createdAt: string;
}

// Colour palette for defect distribution pie chart (cycles through when there are more than 4 types)
const DEFECT_COLORS = ['#f44336', '#ff9800', '#2196f3', '#4caf50', '#9c27b0', '#00bcd4'];

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

// Empty form state for new inspection dialog
const EMPTY_INSPECTION = {
  productCode: '',
  productName: '',
  inspector: '',
  workOrderId: '',
  checkType: 'in_process' as QualityCheck['checkType'],
  notes: '',
};

// Empty form state for new defect dialog
const EMPTY_DEFECT = {
  productCode: '',
  workOrderId: '',
  defectType: '',
  defectDescription: '',
  severity: 'minor' as DefectRecord['severity'],
  quantity: 1,
  reportedBy: '',
  rootCause: '',
  correctiveAction: '',
};

const Quality: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    qualityChecks,
    defectRecords,
    defectTypes,
    metrics,
    isLoading,
    error,
    controlPlans,
    controlPlansLoading,
  } = useSelector((state: RootState) => state.quality);

  const [selectedTab, setSelectedTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCheck, setChecked] = useState<QualityCheck | null>(null);

  // Quality trend data fetched from analytics API
  const [qualityTrends, setQualityTrends] = useState<Array<{ date: string; passRate: number; defectRate: number }>>([]);

  // Create inspection dialog
  const [createInspectionOpen, setCreateInspectionOpen] = useState(false);
  const [newInspection, setNewInspection] = useState({ ...EMPTY_INSPECTION });
  const [createInspectionLoading, setCreateInspectionLoading] = useState(false);
  const [createInspectionError, setCreateInspectionError] = useState<string | null>(null);

  // Create defect dialog
  const [createDefectOpen, setCreateDefectOpen] = useState(false);
  const [newDefect, setNewDefect] = useState({ ...EMPTY_DEFECT });
  const [createDefectLoading, setCreateDefectLoading] = useState(false);
  const [createDefectError, setCreateDefectError] = useState<string | null>(null);

  // Edit check dialog
  const [editCheckOpen, setEditCheckOpen] = useState(false);
  const [editCheckForm, setEditCheckForm] = useState<Partial<QualityCheck>>({});
  const [editCheckLoading, setEditCheckLoading] = useState(false);
  const [editCheckError, setEditCheckError] = useState<string | null>(null);

  // Control plans dialog
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({ planName: '', itemId: '', version: '1.0', description: '' });
  const [createPlanLoading, setCreatePlanLoading] = useState(false);
  const [createPlanError, setCreatePlanError] = useState<string | null>(null);

  // Control plan view dialog
  const [viewPlanOpen, setViewPlanOpen] = useState(false);
  const [viewedPlan, setViewedPlan] = useState<ControlPlan | null>(null);

  // Snackbar for success/error feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch all quality data on mount
  useEffect(() => {
    dispatch(fetchQualityChecks());
    dispatch(fetchDefectRecords());
    dispatch(fetchDefectTypes());
    dispatch(fetchQualityMetrics());
    dispatch(fetchControlPlans());

    // Fetch quality trend data from analytics API
    const token = localStorage.getItem('token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${API_BASE}/analytics/trends/quality?days=7`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          const list = data.data ?? data;
          if (Array.isArray(list) && list.length > 0) {
            setQualityTrends(list.map((t: any) => ({
              date: t.date ?? t.period ?? '',
              passRate: t.passRate ?? t.pass_rate ?? (100 - (t.defectRate ?? 0)),
              defectRate: t.defectRate ?? t.defect_rate ?? 0,
            })));
          }
        }
      })
      .catch(() => {/* trend data failure is non-critical */});
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchQualityChecks());
    dispatch(fetchDefectRecords());
    dispatch(fetchQualityMetrics());
  };

  // ---- Status helpers ----

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'passed': case 'pass': case 'resolved': case 'closed': return 'success';
      case 'failed': case 'fail': case 'open':                    return 'error';
      case 'warning': case 'investigating':                       return 'warning';
      case 'pending': case 'in_progress': case 'on_hold':         return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': case 'pass': return <CheckCircle />;
      case 'failed': case 'fail': return <ErrorIcon />;
      case 'warning':             return <Warning />;
      default:                    return <Assignment />;
    }
  };

  const getSeverityColor = (severity: DefectRecord['severity']): any => {
    switch (severity) {
      case 'minor':    return 'info';
      case 'major':    return 'warning';
      case 'critical': return 'error';
      default:         return 'default';
    }
  };

  const handleViewDetails = (check: QualityCheck) => {
    setChecked(check);
    setDialogOpen(true);
  };

  // ---- Derived metrics (fallback to Redux metrics when available) ----

  const totalChecks = metrics?.totalChecks ?? qualityChecks.length;
  const passedChecks = qualityChecks.filter((c: QualityCheck) => c.overallResult === 'pass').length;
  const failedChecks = metrics?.failedChecks ?? qualityChecks.filter((c: QualityCheck) => c.overallResult === 'fail').length;
  const passRate = metrics?.passRate ?? (totalChecks > 0 ? (passedChecks / totalChecks) * 100 : 0);
  const defectRate = metrics?.defectRate ?? (totalChecks > 0 ? (failedChecks / totalChecks) * 100 : 0);

  const openDefects = defectRecords.filter(
    (d: DefectRecord) => d.status === 'open' || d.status === 'investigating'
  ).length;
  const resolvedDefects = defectRecords.filter(
    (d: DefectRecord) => d.status === 'resolved' || d.status === 'closed'
  ).length;

  // Build defect distribution for pie chart from real defect data only
  const defectDistribution =
    defectRecords.length > 0
      ? Object.entries(
          defectRecords.reduce((acc: Record<string, number>, d: DefectRecord) => {
            const key = d.defectType || 'Other';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {})
        ).map(([name, value], i) => ({
          name,
          value: value as number,
          color: DEFECT_COLORS[i % DEFECT_COLORS.length],
        }))
      : [];

  // ---- Create handlers ----

  const handleCreateInspection = async () => {
    setCreateInspectionLoading(true);
    setCreateInspectionError(null);
    try {
      await dispatch(createInspection({
        ...newInspection,
        checkDate: new Date().toISOString(),
        status: 'pending',
        overallResult: 'pending',
        parameters: [],
      } as Partial<QualityCheck>)).unwrap();
      setCreateInspectionOpen(false);
      setNewInspection({ ...EMPTY_INSPECTION });
    } catch (err: any) {
      setCreateInspectionError(err?.message ?? 'Failed to create inspection. Please try again.');
    } finally {
      setCreateInspectionLoading(false);
    }
  };

  const handleCreateDefect = async () => {
    setCreateDefectLoading(true);
    setCreateDefectError(null);
    try {
      await dispatch(createDefect({
        ...newDefect,
        reportedDate: new Date().toISOString(),
        status: 'open',
      } as Partial<DefectRecord>)).unwrap();
      setCreateDefectOpen(false);
      setNewDefect({ ...EMPTY_DEFECT });
    } catch (err: any) {
      setCreateDefectError(err?.message ?? 'Failed to report defect. Please try again.');
    } finally {
      setCreateDefectLoading(false);
    }
  };

  const handleEditCheckSave = async () => {
    if (!selectedCheck) return;
    setEditCheckLoading(true);
    setEditCheckError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/quality/quality-checks/${selectedCheck.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(editCheckForm),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const _err = body?.message ?? `Failed to update check (HTTP ${res.status})`; throw { message: _err };
      }
      dispatch(fetchQualityChecks());
      setEditCheckOpen(false);
      setDialogOpen(false);
    } catch (err: any) {
      setEditCheckError(err?.message ?? 'Failed to update quality check. Please try again.');
    } finally {
      setEditCheckLoading(false);
    }
  };

  const handleDefectStatusChange = async (defectId: string, newStatus: DefectRecord['status']) => {
    try {
      await dispatch(updateDefectStatus({ id: defectId, status: newStatus })).unwrap();
      dispatch(fetchDefectRecords());
      showSnackbar('Defect status updated');
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update defect status', 'error');
    }
  };

  const handleCreatePlan = async () => {
    setCreatePlanLoading(true);
    setCreatePlanError(null);
    try {
      await dispatch(createControlPlan({
        planName: newPlan.planName,
        itemId: newPlan.itemId,
        version: newPlan.version || '1.0',
        description: newPlan.description || undefined,
      })).unwrap();
      setCreatePlanOpen(false);
      setNewPlan({ planName: '', itemId: '', version: '1.0', description: '' });
      showSnackbar('Control plan created successfully');
    } catch (err: any) {
      setCreatePlanError(err?.message ?? 'Failed to create control plan. Please try again.');
    } finally {
      setCreatePlanLoading(false);
    }
  };

  const handleUpdatePlanStatus = async (planId: string, status: 'active' | 'inactive') => {
    try {
      await dispatch(updateControlPlan({ id: planId, status })).unwrap();
      showSnackbar(`Control plan ${status === 'active' ? 'activated' : 'deactivated'}`);
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update control plan', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        section="Quality"
        title="Quality Management"
        subtitle="Control plans, inspections with e-signatures, NCRs, scrap & rework, SPC."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? 'Loading…' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.open(`${REPORT_BASE}/quality/gauges/register/report`, '_blank')}
            >
              IMTP Register
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateDefectOpen(true)}
            >
              Report Defect
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateInspectionOpen(true)}
              sx={{ bgcolor: '#f2b32c', color: '#1a1a1a', fontWeight: 700, '&:hover': { bgcolor: '#e0a41f' } }}
            >
              New Quality Check
            </Button>
          </>
        }
      />

      {/* Error banner */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading bar */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Quality Pass Rate
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {isLoading && !metrics ? (
                      <CircularProgress size={28} />
                    ) : (
                      `${passRate.toFixed(1)}%`
                    )}
                  </Typography>
                </Box>
                <Verified sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Checks
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {isLoading && qualityChecks.length === 0 ? (
                      <CircularProgress size={28} />
                    ) : (
                      totalChecks
                    )}
                  </Typography>
                </Box>
                <Science sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Open Defects
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {isLoading && defectRecords.length === 0 ? (
                      <CircularProgress size={28} />
                    ) : (
                      openDefects
                    )}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Resolved Defects
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {isLoading && defectRecords.length === 0 ? (
                      <CircularProgress size={28} />
                    ) : (
                      resolvedDefects
                    )}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`Quality Checks (${qualityChecks.length})`} />
          <Tab label={`Defect Records (${defectRecords.length})`} />
          <Tab label="Quality Analytics" />
          <Tab label={`Control Plans (${controlPlans.length})`} />
          <Tab label="SPC Charts" />
        </Tabs>
      </Paper>

      {/* Quality Checks Tab */}
      {selectedTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Quality Checks</Typography>
          {isLoading && qualityChecks.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : qualityChecks.length === 0 ? (
            <Alert severity="info">No quality checks found. Create one to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Check Number</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Inspector</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(qualityChecks as QualityCheck[]).map((check) => (
                    <TableRow key={check.id}>
                      <TableCell>{check.checkNumber}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{check.productName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {check.productCode}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{check.inspector}</TableCell>
                      <TableCell>
                        <Chip
                          label={check.checkType.replace('_', ' ').toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={check.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(check.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={check.overallResult.toUpperCase()}
                          color={getStatusColor(check.overallResult)}
                          size="small"
                          icon={getStatusIcon(check.overallResult)}
                        />
                      </TableCell>
                      <TableCell>
                        {check.checkDate ? new Date(check.checkDate).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleViewDetails(check)} title="View details">
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Defect Records Tab */}
      {selectedTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Defect Records</Typography>
          {isLoading && defectRecords.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : defectRecords.length === 0 ? (
            <Alert severity="info">No defect records found.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Defect Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(defectRecords as DefectRecord[]).map((defect) => (
                    <TableRow key={defect.id}>
                      <TableCell>{defect.productCode}</TableCell>
                      <TableCell>{defect.defectType}</TableCell>
                      <TableCell>{defect.defectDescription}</TableCell>
                      <TableCell>
                        <Chip
                          label={defect.severity.toUpperCase()}
                          color={getSeverityColor(defect.severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{defect.quantity}</TableCell>
                      <TableCell>
                        <FormControl size="small" variant="outlined" sx={{ minWidth: 140 }}>
                          <Select
                            value={defect.status}
                            onChange={(e) => handleDefectStatusChange(defect.id, e.target.value as DefectRecord['status'])}
                            renderValue={(val) => (
                              <Chip
                                label={(val as string).replace('_', ' ').toUpperCase()}
                                color={getStatusColor(val as string)}
                                size="small"
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                          >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="investigating">Investigating</MenuItem>
                            <MenuItem value="resolved">Resolved</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>{defect.reportedBy}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {defect.reportedDate ? new Date(defect.reportedDate).toLocaleDateString() : '—'}
                          <IconButton
                            size="small"
                            title="Print NCR report"
                            onClick={() => window.open(`${REPORT_BASE}/quality/defects/${defect.id}/report`, '_blank')}
                          >
                            <Print sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Quality Analytics Tab */}
      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {/* Defect Rate Summary */}
          {metrics && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Pass Rate: <strong>{metrics.passRate.toFixed(1)}%</strong> &nbsp;|&nbsp;
                Defect Rate: <strong>{metrics.defectRate.toFixed(1)}%</strong> &nbsp;|&nbsp;
                Total Checks: <strong>{metrics.totalChecks}</strong> &nbsp;|&nbsp;
                Failed: <strong>{metrics.failedChecks}</strong>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Quality Trends (Last 7 Days)
              </Typography>
              {qualityTrends.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No quality trend data available from the analytics service.
                </Alert>
              ) : (
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={qualityTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="passRate"
                    stroke="#4caf50"
                    strokeWidth={3}
                    name="Pass Rate (%)"
                  />
                  <Line
                    type="monotone"
                    dataKey="defectRate"
                    stroke="#f44336"
                    strokeWidth={2}
                    name="Defect Rate (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Defect Distribution
              </Typography>
              {defectDistribution.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No defect records available to display distribution.
                </Alert>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="70%">
                    <PieChart>
                      <Pie
                        data={defectDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {defectDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box mt={2}>
                    {defectDistribution.map((item) => (
                      <Box key={item.name} display="flex" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center">
                          <Box
                            width={12}
                            height={12}
                            bgcolor={item.color}
                            borderRadius="50%"
                            mr={1}
                          />
                          <Typography variant="body2">{item.name}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Control Plans Tab */}
      {selectedTab === 3 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Control Plans</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreatePlanOpen(true)}
            >
              New Control Plan
            </Button>
          </Box>
          {controlPlansLoading && controlPlans.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : controlPlans.length === 0 ? (
            <Alert severity="info">No control plans found. Create one to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>Item / Product</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(controlPlans as ControlPlan[]).map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>{plan.planName}</TableCell>
                      <TableCell>{plan.itemId}</TableCell>
                      <TableCell>{plan.version}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.status.toUpperCase()}
                          color={plan.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} alignItems="center">
                          <IconButton
                            size="small"
                            title="Print control plan"
                            onClick={() => window.open(`${REPORT_BASE}/quality/control-plans/${plan.id}/report`, '_blank')}
                          >
                            <Print sx={{ fontSize: 16 }} />
                          </IconButton>
                          {plan.status === 'inactive' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleUpdatePlanStatus(plan.id, 'active')}
                            >
                              Activate
                            </Button>
                          )}
                          {plan.status === 'active' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleUpdatePlanStatus(plan.id, 'inactive')}
                            >
                              Deactivate
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            title="View details"
                            onClick={() => { setViewedPlan(plan); setViewPlanOpen(true); }}
                          >
                            <Visibility />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* SPC Charts Tab */}
      {selectedTab === 4 && (
        <Box>
          <SPCCharts />
        </Box>
      )}

      {/* Quality Check Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Quality Check Details</DialogTitle>
        <DialogContent>
          {selectedCheck && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Check Number</Typography>
                  <Typography variant="body1">{selectedCheck.checkNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Product</Typography>
                  <Typography variant="body1">
                    {selectedCheck.productName} ({selectedCheck.productCode})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Inspector</Typography>
                  <Typography variant="body1">{selectedCheck.inspector}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Check Date</Typography>
                  <Typography variant="body1">
                    {selectedCheck.checkDate ? new Date(selectedCheck.checkDate).toLocaleString() : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedCheck.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(selectedCheck.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Overall Result</Typography>
                  <Chip
                    label={selectedCheck.overallResult.toUpperCase()}
                    color={getStatusColor(selectedCheck.overallResult)}
                    size="small"
                    icon={getStatusIcon(selectedCheck.overallResult)}
                  />
                </Grid>
              </Grid>

              {selectedCheck.parameters && selectedCheck.parameters.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Quality Parameters</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parameter</TableCell>
                          <TableCell>Specification</TableCell>
                          <TableCell>Measured Value</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCheck.parameters.map((param) => (
                          <TableRow key={param.id}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>{param.specification}</TableCell>
                            <TableCell>
                              {param.measuredValue} {param.unit}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={param.status.toUpperCase()}
                                color={getStatusColor(param.status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {selectedCheck.notes && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedCheck.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditCheckForm({ status: selectedCheck?.status, notes: selectedCheck?.notes });
              setEditCheckOpen(true);
            }}
          >
            Edit Check
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Check Dialog */}
      <Dialog open={editCheckOpen} onClose={() => setEditCheckOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Quality Check</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {editCheckError && (
              <Alert severity="error" sx={{ mb: 2 }}>{editCheckError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editCheckForm.status ?? ''}
                    label="Status"
                    onChange={(e) => setEditCheckForm(prev => ({
                      ...prev,
                      status: e.target.value as QualityCheck['status'],
                    }))}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="passed">Passed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="on_hold">On Hold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={editCheckForm.notes ?? ''}
                  onChange={(e) => setEditCheckForm(prev => ({ ...prev, notes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Result"
                  value={(editCheckForm as any).result ?? ''}
                  onChange={(e) => setEditCheckForm(prev => ({ ...prev, result: e.target.value } as any))}
                  fullWidth
                  placeholder="e.g. pass / fail / pending"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditCheckOpen(false); setEditCheckError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditCheckSave}
            disabled={editCheckLoading}
            startIcon={editCheckLoading ? <CircularProgress size={16} /> : <Edit />}
          >
            {editCheckLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Inspection Dialog */}
      <Dialog
        open={createInspectionOpen}
        onClose={() => setCreateInspectionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Quality Check</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createInspectionError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createInspectionError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Product Code"
                  value={newInspection.productCode}
                  onChange={(e) => setNewInspection(prev => ({ ...prev, productCode: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Product Name"
                  value={newInspection.productName}
                  onChange={(e) => setNewInspection(prev => ({ ...prev, productName: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Inspector"
                  value={newInspection.inspector}
                  onChange={(e) => setNewInspection(prev => ({ ...prev, inspector: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Work Order ID"
                  value={newInspection.workOrderId}
                  onChange={(e) => setNewInspection(prev => ({ ...prev, workOrderId: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Check Type</InputLabel>
                  <Select
                    value={newInspection.checkType}
                    label="Check Type"
                    onChange={(e) => setNewInspection(prev => ({
                      ...prev,
                      checkType: e.target.value as QualityCheck['checkType'],
                    }))}
                  >
                    <MenuItem value="incoming">Incoming</MenuItem>
                    <MenuItem value="in_process">In Process</MenuItem>
                    <MenuItem value="final">Final</MenuItem>
                    <MenuItem value="audit">Audit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={newInspection.notes}
                  onChange={(e) => setNewInspection(prev => ({ ...prev, notes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateInspectionOpen(false); setNewInspection({ ...EMPTY_INSPECTION }); setCreateInspectionError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateInspection}
            disabled={createInspectionLoading || !newInspection.productCode || !newInspection.inspector}
            startIcon={createInspectionLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createInspectionLoading ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Defect Dialog */}
      <Dialog
        open={createDefectOpen}
        onClose={() => setCreateDefectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report New Defect</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createDefectError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createDefectError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Product Code"
                  value={newDefect.productCode}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, productCode: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Work Order ID"
                  value={newDefect.workOrderId}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, workOrderId: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Defect Type"
                  value={newDefect.defectType}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, defectType: e.target.value }))}
                  fullWidth
                  required
                  placeholder="e.g. Dimensional, Surface"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={newDefect.severity}
                    label="Severity"
                    onChange={(e) => setNewDefect(prev => ({
                      ...prev,
                      severity: e.target.value as DefectRecord['severity'],
                    }))}
                  >
                    <MenuItem value="minor">Minor</MenuItem>
                    <MenuItem value="major">Major</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Defect Description"
                  value={newDefect.defectDescription}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, defectDescription: e.target.value }))}
                  fullWidth
                  required
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={newDefect.quantity}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reported By"
                  value={newDefect.reportedBy}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, reportedBy: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Root Cause (optional)"
                  value={newDefect.rootCause}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, rootCause: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Corrective Action (optional)"
                  value={newDefect.correctiveAction}
                  onChange={(e) => setNewDefect(prev => ({ ...prev, correctiveAction: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDefectOpen(false); setNewDefect({ ...EMPTY_DEFECT }); setCreateDefectError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCreateDefect}
            disabled={createDefectLoading || !newDefect.productCode || !newDefect.defectType || !newDefect.reportedBy}
            startIcon={createDefectLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createDefectLoading ? 'Reporting…' : 'Report Defect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Control Plan Dialog */}
      <Dialog
        open={createPlanOpen}
        onClose={() => setCreatePlanOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Control Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createPlanError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createPlanError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Plan Name"
                  value={newPlan.planName}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, planName: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Item ID"
                  value={newPlan.itemId}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, itemId: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Version"
                  value={newPlan.version}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, version: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={newPlan.description}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, description: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreatePlanOpen(false); setNewPlan({ planName: '', itemId: '', version: '1.0', description: '' }); setCreatePlanError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePlan}
            disabled={createPlanLoading || !newPlan.planName || !newPlan.itemId}
            startIcon={createPlanLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createPlanLoading ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Control Plan Dialog */}
      <Dialog
        open={viewPlanOpen}
        onClose={() => setViewPlanOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Control Plan Details</DialogTitle>
        <DialogContent>
          {viewedPlan && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Plan Name</Typography>
                <Typography variant="body1">{viewedPlan.planName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Item ID</Typography>
                <Typography variant="body1">{viewedPlan.itemId}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Version</Typography>
                <Typography variant="body1">{viewedPlan.version}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={viewedPlan.status.toUpperCase()}
                  color={viewedPlan.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              {viewedPlan.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{viewedPlan.description}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Created</Typography>
                <Typography variant="body1">
                  {viewedPlan.createdAt ? new Date(viewedPlan.createdAt).toLocaleString() : '—'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPlanOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Quality;
